// @flow
import superagent from 'superagent';
import * as querystring from 'query-string';
import type Moment from 'moment';
import ApiClient from './ApiClient';
import { isJp } from '../helpers/location';
import type { LatLngLiteral } from '../components/Map/types';
import type {
  ApplePaymentData,
  ApplePaymentMethod
} from '../components/Payments/types/types';
import type { BookingMadeStatuses } from '../types';

const debug = require('debug')('jp:api:gateway');

const { stringify } = querystring;

export default class Api {
  client: any;

  static consents: {
    get: () => Promise<any>,
    deleteAccount: () => Promise<any>,
    create: (Array<any>) => Promise<any>,
    getTerms: () => Promise<any>,
    getPrivacy: () => Promise<any>
  };

  static geo: {
    getLocation: any,
    search: any
  };

  static locationDetails: {
    reviews: any,
    info: any,
    listingDetails: any
  };

  static vehicles: {
    getVehicles: any,
    registrationLookup: any
  };

  constructor(req: any, agent: any, res: any = null) {
    this.client = new ApiClient(req, agent, res);
  }

  me() {
    debug('me');
    return this.client.get('/v5/me?include=booking_summary');
  }

  amendProfile(firstName: string, lastName: string, phone: string) {
    debug('amendProfile', firstName, lastName, phone);

    return this.client.patch('/v5/users/amend', {
      data: {
        firstName,
        lastName,
        phone
      }
    });
  }

  createUpdateAddress({
    houseNo,
    address1,
    address2,
    address3,
    city,
    state,
    postalCode,
    country
  }) {
    debug('createUpdateAddress');

    return this.client.post('/v5/users/address', {
      data: {
        house_no: houseNo,
        address1,
        address2,
        address3,
        city,
        state,
        postal_code: postalCode,
        country
      },
      disableRequestTransform: true
    });
  }

  sendEmailVerification() {
    debug('sendEmailVerification');
    return this.client.post('/v5/verify/email-resend');
  }

  changeEmail(newEmail: string, password: string) {
    debug('changeEmail', newEmail, password);
    return this.client.patch('/v5/users/change-email', {
      data: {
        newEmail,
        password
      }
    });
  }

  track(data: any) {
    debug('track', data);
    return this.client.post('/v5/users/tracking', {
      data
    });
  }

  longSession(data: any) {
    debug('longSession', data);
    return this.client.post('/v5/users/session', {
      data
    });
  }

  login(params: any, email: string, password: string, recaptchaToken: string) {
    debug('login', { ...params }, email);
    return this.client.post('/v5/auth/login', {
      data: {
        params,
        username: email,
        password,
        'g-recaptcha-response': recaptchaToken
      }
    });
  }

  google(accessToken: string, queryParams: any) {
    debug('google');
    return this.client.post('/v5/auth/google', {
      data: {
        access_token: accessToken,
        params: queryParams
      }
    });
  }

  facebook(accessToken: string, queryParams: any) {
    debug('facebook');
    return this.client.post('/v5/auth/facebook', {
      data: {
        accessToken,
        params: queryParams
      }
    });
  }

  appleLogin(
    accessToken: string,
    code: string,
    firstName: ?string,
    lastName: ?string
  ) {
    debug('apple login', { accessToken, code, firstName, lastName });
    return this.client.post('/v5/auth/apple', {
      data: {
        access_token: accessToken,
        code,
        firstname: firstName,
        lastname: lastName
      }
    });
  }

  resetPassword(username: string) {
    debug('password-reset');
    return this.client.post('/v4/auth/password-reset', {
      data: {
        username
      }
    });
  }

  profile() {
    debug('profile');
    return this.client.get('/v4/users/profile');
  }

  postcodeLookup(country: string, postcode: string) {
    debug('postcode-lookup');
    return this.client.post('/v4/utilities/geo/postcode-lookup', {
      data: {
        postcode,
        country
      }
    });
  }

  resolveAddress(addressId: string) {
    debug('resolve-address');
    return this.client.post('/v4/utilities/geo/resolve-address', {
      data: {
        addressId
      },
      disableRequestTransform: true
    });
  }

  register(
    params: any,
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    recaptchaToken: string
  ) {
    debug(
      'register',
      { ...params },
      email,
      firstName,
      lastName,
      recaptchaToken
    );
    return this.client.post('/v4/auth/register', {
      data: {
        params,
        email,
        password,
        firstName,
        lastName,
        authCookie: true,
        'g-recaptcha-response': recaptchaToken
      }
    });
  }

  logout() {
    debug('logout');
    return this.client.post('/v5/auth/logout');
  }

  getDriveUpListings(id: string) {
    debug('getDriveUpListings', id);
    return this.client.get('/v4/listings/driveup', {
      params: {
        'filter[id]': id
      }
    });
  }

  checkout = {
    // This can only handle one set of dates atm
    summary: (data: any): Promise<*> => {
      debug('checkout.summary raw', data);
      const { monthlyDuration } = data;
      const postData = {
        quoteId: data.quoteId,
        listingId: data.listingId,
        vrm: data.vrm,
        dates: [
          {
            startDate: data.startDate.format(),
            ...(data.endDate && { endDate: data.endDate.format() }),
            ...(monthlyDuration !== null && { duration: monthlyDuration })
          }
        ]
      };

      debug('checkout.summary refined', postData);
      return this.client.post('/v5/checkout/summary', {
        data: {
          ...postData
        }
      });
    },
    book: (data: any, payOnArrival: boolean): Promise<*> => {
      debug('checkout.book', data);

      return this.client.post(`/v5/checkout`, {
        data: {
          payOnArrival,
          paymentSourceId: data.paymentId,
          quoteId: data.quoteId
        },
        params: {
          include:
            'barrier_tokens,listing.address,listing.photos,vehicle,listing,driver,awin,review'
        }
      });
    },
    addPromo: (promoCode: string): Promise<*> => {
      debug('checkout.addPromo', promoCode);

      return this.client.post(`/v4/promotions`, {
        data: {
          promo_code: promoCode
        }
      });
    },

    partnerWhiteLabel: (partnerName: string): Promise<*> => {
      debug('checkout.partnerWhitelabel', partnerName);

      return this.client.get(`/v5/whitelabel/${partnerName.toLowerCase()}`);
    }
  };

  geo = {
    getLocation: (slug: string, locale: string): Promise<*> => {
      debug('geo.getLocation', slug, locale);
      return this.client.get(`/v5/geo/${locale}/${slug}`);
    },

    search: (data: any): Promise<*> => {
      debug('geo.search', data);

      return this.client.get('/v5/search', {
        params: {
          ...data,
          cluster: 0,
          driveup: 1
        }
      });
    }
  };

  routing = {
    walking: (
      destination: LatLngLiteral,
      listingIds: Array<string | number>
    ): Promise<*> => {
      debug('routing.walking', destination, listingIds);

      return this.client.get('/v5/routing/walking', {
        params: stringify(
          {
            destination: `${destination.lat},${destination.lng}`,
            listing_ids: listingIds
          },
          { arrayFormat: 'bracket' }
        )
      });
    }
  };

  locationDetails = {
    reviews: (id: number): Promise<*> => {
      debug('getReviews', id);
      return this.client.get(`/v4/listings/${id}/reviews`);
    },

    // Get Listing details on search
    info: (type: string, id: number): Promise<*> => {
      debug('getLocationInfo', type, id);
      if (isJp({ type })) {
        return this.client.get(`/v4/listings/${id}`);
      }
      return this.client.get(`/v4/data/listing/${id}`);
    },

    // Get listing details on checkout
    listingDetails: (id: number): Promise<*> =>
      this.client.get(`/v4/listings/${id}`)
  };

  vehicles = {
    getVehicles: () => {
      debug('getVehicles');
      return this.client.get('/v4/users/vehicles');
    },

    registrationLookup: (registration: string) => {
      debug('vehicles.registrationLookup', registration);
      return this.client.get('/v4/vehicles/registration-lookup', {
        params: {
          registration
        }
      });
    },

    create: (data: any) => {
      debug('vehicles.create', data);
      return this.client.post('/v4/users/vehicles', {
        data
      });
    },

    deleteVehicle: (id: number) => {
      debug('vehicles.delete', id);
      return this.client.del(`/v4/users/vehicles/${id}`);
    }
  };

  paymentMethods = {
    tokenizeGooglePay: (
      signature: string,
      protocolVersion: string,
      signedMessage: string
    ) => {
      debug('tokenize google pay', signature, protocolVersion, signedMessage);

      return this.client.post('/v5/payments/tokenize/wallet', {
        data: {
          googlepay: {
            signature,
            protocolVersion,
            signedMessage
          }
        }
      });
    },
    tokenizeApplePay: (
      paymentData: ApplePaymentData,
      paymentMethod: ApplePaymentMethod
    ) => {
      debug('tokenize apple pay', paymentData, paymentMethod);

      return this.client.post('/v5/payments/tokenize/wallet', {
        data: {
          applepay: {
            data: {
              data: paymentData.data,
              signature: paymentData.signature,
              version: paymentData.version,
              header: paymentData.header
            },
            payment_network: paymentMethod.network,
            payment_instrument_name: paymentMethod.displayName
          }
        },
        disableRequestTransform: true
      });
    },
    validateApplePaySession: (validationUrl: string) => {
      debug('validateApplePaySession');

      return this.client.post('/v5/payments/applepay/session', {
        data: {
          validation_url: validationUrl
        }
      });
    },
    create: (tokenId: string, zip: string) => {
      debug('createPaymentMethod');
      return this.client.post('/v4/users/payment-methods', {
        data: {
          tokenId,
          address: {
            zip
          }
        }
      });
    },
    createGooglePay: (tokenId: string) => {
      debug('createGooglePaymentMethod');
      return this.client.post('/v4/users/payment-methods', {
        data: {
          tokenId,
          googlepay: tokenId
        }
      });
    },
    createApplePay: (tokenId: string) => {
      debug('createApplePaymentMethod');
      return this.client.post('/v4/users/payment-methods', {
        data: {
          tokenId,
          applepay: tokenId
        }
      });
    },
    createPaypal: (nonce: string) => {
      debug('createPaypalMethod');
      return this.client.post('/v4/users/payment-methods', {
        data: {
          nonce
        }
      });
    },
    getPaymentMethods: (listingId?: number) => {
      const id = listingId || '';
      debug('getPaymentMethods', id);
      return this.client.get('/v4/users/payment-methods', {
        params: {
          listing_id: id,
          include_corporate: 1
        }
      });
    },
    deletePaymentMethod: (id: number) => {
      debug('deletePaymentMethod', id);
      return this.client.del(`/v4/users/payment-methods/${id}/`);
    }
  };

  paymentToken = {
    braintree: () => {
      debug('getBraintreeToken');
      return this.client.get('/v4/payments/client-token/BRAINTREE');
    }
  };

  phoneNumbers = {
    create: (countryCode: string, phoneNumber: string, delay: number) => {
      debug('phoneNumbers.create', countryCode, phoneNumber, delay);
      return this.client.post('/v4/users/phone', {
        data: {
          countryCode: `+${countryCode}`,
          phoneNumber,
          delay
        }
      });
    }
  };

  consents = {
    get: () => {
      debug('consents.get');
      return this.client.get('/v5/consents');
    },
    deleteAccount: () => {
      debug('consents.deleteAccount');
      return this.client.post('/v5/consents', {
        data: {
          data: [
            {
              name: 'standard',
              accepted: false,
              version: 1
            }
          ]
        }
      });
    },
    create: (data: any) => {
      debug('consents.create');
      return this.client.post('/v5/consents', {
        data: {
          data
        }
      });
    },
    getTerms: () => {
      debug('consents.terms');
      return this.client.get('/v4/content/terms-and-conditions');
    },
    getPrivacy: () => {
      debug('consents.privacy');
      return this.client.get('/v4/content/privacy-policy');
    }
  };

  seasonTickets = {
    bootstrap: (vendor: string) => {
      debug('seasonTickets.bootstrap', vendor);
      return this.client.get(
        `/v5/season-tickets/vendor/${vendor}?include=prices,season_listings.address,zones`
      );
    },
    checkout: (data: any) => {
      debug('seasonTickets.checkout', data);

      return this.client.post('/v5/season-tickets/checkout', {
        data
      });
    },
    zones: (zoneId: string) => {
      debug('seasonTickets.zones', zoneId);
      return this.client.get(
        `/v5/season-tickets/zone/${zoneId}?include=listings`
      );
    },
    calculatePrice: (
      priceId: string,
      quantity: string,
      wantsRecurring: ?boolean
    ) => {
      debug('seasonTickets.calculatePrice', {
        priceId,
        quantity,
        wantsRecurring
      });
      return this.client.get('/v5/season-tickets/price', {
        params: {
          price_id: priceId,
          quantity,
          ...(wantsRecurring && { wants_recurring: wantsRecurring })
        }
      });
    }
  };

  listings = {
    add: (data: any, token: string) => {
      debug('listings.add', data);

      return this.client.post('/v4/listings/add', {
        data: {
          data: {
            attributes: data,
            g_recaptcha_response: token
          }
        },
        disableRequestTransform: true
      });
    },

    calculateToEarn: (
      dailyPrice: string,
      monthlyPrice: string,
      duration: number,
      durationType: string,
      listingId: number
    ) => {
      const data = {
        daily_price: parseFloat(dailyPrice) * 100,
        monthly_price: parseFloat(monthlyPrice) * 100,
        duration,
        duration_type: durationType,
        listing_id: listingId
      };
      debug('listings.calculateToEarn', data);

      return this.client.post('v5/pricing/duration-calculator', {
        data,
        disableRequestTransform: true
      });
    },

    edit: (listingId: number, data: any, token: string) => {
      debug('listings.edit', data);

      return this.client.patch(`/v4/listings/${listingId}/edit`, {
        data: {
          data: {
            attributes: data,
            g_recaptcha_response: token
          }
        },
        disableRequestTransform: true
      });
    },

    editAutomatedPricing: (listingId: number, data: any) => {
      debug('listings.editAutomatedPricing', data);

      return this.client.post(`/v5/pricing/listing/`, {
        data: {
          ...data,
          listing_id: listingId
        },
        disableRequestTransform: true
      });
    },

    editSlottedPricing: (listingId: number, data: any) => {
      debug('listings.editSlottedPricing', data);

      return this.client.patch(`/v4/pricing/${listingId}/edit`, {
        data: {
          data: {
            attributes: { ...data, listingId },
            id: listingId
          }
        },
        disableRequestTransform: true
      });
    },

    load: (id: number) => {
      debug('listings.load', id);

      return this.client.get(`/v4/listings/${id}`);
    },

    loadPricing: (id: number) => {
      debug('listings.loadPricing', id);

      return this.client.get(`/v5/pricing/listing/${id}`);
    },

    availability: (listingId: string) => {
      debug('listings.availability', listingId);

      return this.client.get(`/v5/listings/${listingId}/availability`);
    },

    deleteAvailability: (availabilityId: string) => {
      debug('listings.deleteAvailability', availabilityId);

      return this.client.del(`/v5/listings/availability/${availabilityId}`);
    },

    updateAvailabilityDays: (
      listingId: string,
      listingAvailabilityDays: Array<any>
    ) => {
      debug('listings.updateAvailabilityDays', listingId);

      return this.client.patch(`/v5/listings/${listingId}/availability`, {
        params: stringify(
          {
            listing_availability_days: listingAvailabilityDays
          },
          { arrayFormat: 'bracket' }
        ),
        disableRequestTransform: true
      });
    },

    updateAvailability: (
      listingId: string,
      start: Moment,
      end: Moment,
      numberOfSpaces: number
    ) => {
      debug(
        'listings.updateAvailability',
        listingId,
        start,
        end,
        numberOfSpaces
      );

      return this.client.patch(`/v5/listings/${listingId}/availability`, {
        params: {
          start: start.format(),
          end: end.format(),
          number_of_spaces: numberOfSpaces
        },
        disableRequestTransform: true
      });
    },

    deactivate: (listingId: string, reason: string, details?: string) => {
      debug('listings.deactivate', reason, details);
      return this.client.post(`/v5/listings/${listingId}/deactivate`, {
        data: {
          reason,
          details
        }
      });
    },

    activate: (listingId: string) => {
      debug('listings.activate');
      return this.client.post(`/v5/listings/${listingId}/activate`);
    },

    pendingReasons: (listingId: string) => {
      debug('listings.pendingReasons', listingId);
      return this.client.post(`/v5/listings/pending-reasons`, {
        data: {
          listing_id: listingId
        }
      });
    },

    status: (pageNumber: string = '1', isDeleted: boolean = false) => {
      debug('listings.status');
      return this.client.get(
        `/v5/listings/?page=${pageNumber}&is_deleted=${!isDeleted ? '0' : '1'}`
      );
    },

    pricingSwitch: (
      listingId: string,
      pricingType: 'SLOTTED' | 'TARIFF_BOARD'
    ) => {
      debug('listings.pricingSwitch', listingId, pricingType);

      return this.client.post(`/v5/pricing/switch`, {
        data: {
          listing_id: listingId,
          price_type: pricingType
        }
      });
    }
  };

  withdrawals = {
    getWithdrawals: () => {
      debug('getWithdrawals');
      return this.client.get('/v4/users/withdrawal-methods');
    },
    create: (data: any) => {
      debug('withdrawals.create', data);
      return this.client.post('/v4/users/withdrawal-methods', {
        data: {
          data: {
            attributes: data
          }
        },
        disableRequestTransform: true
      });
    },
    delete: (id: number) => {
      debug('withdrawals.delete', id);
      return this.client.del(`/v4/users/withdrawal-methods/${id}`);
    },
    validateBank: (data: any) => {
      debug('withdrawals.validateBank', data);
      return this.client.post('/v4/users/withdrawal-methods/validate-bank', {
        data,
        disableRequestTransform: true
      });
    },
    editWithdrawalFrequency: (frequency: string) => {
      debug('withdrawals.editWithdrawalFrequency', frequency);
      const data = {
        frequency
      };

      return this.client.put('/v4/users/withdrawal', {
        data,
        disableRequestTransform: true
      });
    },
    getWithdrawalFrequency: () => {
      debug('withdrawals.get');
      return this.client.get('/v4/users/withdrawal');
    },
    editWithdrawalMethod: (id: number) => {
      debug('withdrawals.edit');

      return this.client.patch(`/v4/users/withdrawal-methods/${id}`, {
        is_primary: true
      });
    }
  };

  bookings = {
    getBooking: (bookingId: number, include: Array<string>) => {
      debug('getBooking');
      return this.client.get(`/v5/bookings/${bookingId}`, {
        params: {
          include: include.join(',')
        }
      });
    },
    getBookingPayments: (bookingId: number) => {
      debug('getBookingPayments', bookingId);
      return this.client.get(`/v5/bookings/${bookingId}/payments`);
    },
    openBarrier: (bookingId: string) => {
      debug('openBarrier', bookingId);
      return this.client.post(`/v4/barriers/${bookingId}`);
    },
    changeVehicle: (
      bookingId: number,
      vehicleId: number,
      include: Array<string>
    ) => {
      debug('changeVehicle', bookingId, vehicleId);
      return this.client.post(`/v5/bookings/${bookingId}/vehicle/change`, {
        data: {
          vehicle_id: vehicleId
        },
        params: {
          include: include.join(',')
        }
      });
    },
    getBookingsMade: (
      pageNumber: ?string = '1',
      statuses: Array<BookingMadeStatuses>,
      includePrice: boolean = true,
      includePaymentSource: boolean = true
    ) => {
      debug('getBookingsMade', pageNumber, statuses);
      const include = [];

      if (includePrice) {
        include.push('driver_price');
      }
      if (includePaymentSource) {
        include.push('payment_source');
      }

      return this.client.get('/v5/bookings/made', {
        params: {
          page: pageNumber,
          'status[]': statuses,
          include: include.join(','),
          per_page: '7'
        }
      });
    },
    addReview: (bookingId: string, rating: number, comment: string) => {
      debug('addReview', bookingId);
      return this.client.post(`/v4/users/bookings/${bookingId}/review`, {
        params: {
          rating,
          comment
        }
      });
    }
  };
}

export const client = new Api(null, superagent);

// Using this function instead of the above exported singleton make it easier to mock
export const getClient = () => client;
